import React from "react";

import { Validator, Value } from "lib/build/schema";

import { api } from "src/common/utils/api";
import { useUpdateUser } from "src/features/auth";
import { FormState, useFormState } from "src/features/forms";
import {
	useCreateGenericError,
	useCreateMessage
} from "src/features/notifications";
import { messages } from "src/views/user-profile/messages";

export function useForm(
	initialValue: Value,
	validator: Validator
): {
	formState: FormState;
	isSubmitted: boolean;
	isLoading: boolean;
	onSubmit(): Promise<void>;
} {
	const formState = useFormState({ validator, initialValue });

	const formStateRef = React.useRef(formState);
	React.useEffect(() => {
		formStateRef.current = formState;
	}, [formState]);

	const [{ isSubmitted, isLoading }, setState] = React.useState({
		isSubmitted: false,
		isLoading: false
	});

	const createMessage = useCreateMessage();
	const createGenericError = useCreateGenericError();
	const updateUser = useUpdateUser();

	const onSubmit = React.useCallback(async () => {
		setState(state => ({ ...state, isSubmitted: true }));
		const { current: formState } = formStateRef;
		const {
			errors,
			value: {
				title,
				firstName,
				lastName,
				phoneNumber,
				occupation,
				stampNumber,
				defaultLanguage,
				currentPassword,
				newPassword
			}
		} = formState;
		if (errors.length === 0) {
			setState(state => ({ ...state, isLoading: true }));
			try {
				await api.put<Value>("/profile", {
					title,
					firstName,
					lastName,
					phoneNumber,
					occupation,
					stampNumber,
					defaultLanguage
				});
				if (newPassword) {
					await api.put<Value>("/profile/password", {
						currentPassword,
						newPassword
					});
					formStateRef.current.setValue((value: Value) => ({
						...value,
						currentPassword: "",
						newPassword: "",
						newPasswordAgain: ""
					}));
				}
				setState(state => ({ ...state, isLoading: false }));
				createMessage(messages.success);
				updateUser({
					firstName,
					lastName,
					occupation,
					defaultLanguage
				});
			} catch {
				setState(state => ({ ...state, isLoading: false }));
				createGenericError();
			}
		}
	}, [createMessage, createGenericError, updateUser]);

	return { formState, isSubmitted, isLoading, onSubmit };
}
